import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Table
} from "reactstrap";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";
import {
  useGetPayrollQuery,
  useGeneratePayrollMutation,
  useCreatePayrollMutation,
  useUpdatePayrollMutation,
  useGetEmployeesQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation
} from "store/features/attendance/attendanceApi";
import { useGetAllUsersQuery } from "store/features/users/userManagementApi";

const PayrollManagement = () => {
  const user = useSelector(selectUser);
  const cleanRole = String(user?.role || "").toLowerCase().trim().replace(/[-\s]/g, "_");
  const isAdmin = cleanRole === "super_admin" || cleanRole === "admin" || cleanRole === "hr";

  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [payslipModalOpen, setPayslipModalOpen] = useState(false);

  // Salary Setting Modal State
  const [salaryModalOpen, setSalaryModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [salaryInput, setSalaryInput] = useState("");
  const [departmentInput, setDepartmentInput] = useState("");
  const [designationInput, setDesignationInput] = useState("");

  const { data: payrollData = [], isLoading: isPayrollLoading } = useGetPayrollQuery({ month: selectedMonth });
  const { data: users = [] } = useGetAllUsersQuery();
  const { data: employees = [] } = useGetEmployeesQuery();

  const [generatePayroll, { isLoading: isGenerating }] = useGeneratePayrollMutation();
  const [createPayroll, { isLoading: isCreatingSingle }] = useCreatePayrollMutation();
  const [updatePayroll] = useUpdatePayrollMutation();
  const [createEmployee, { isLoading: isCreatingEmp }] = useCreateEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdatingEmp }] = useUpdateEmployeeMutation();

  const handleGeneratePayroll = async () => {
    try {
      await generatePayroll({ month: selectedMonth }).unwrap();
    } catch (err) {
      toast.error(err?.data?.error || "Failed to generate payroll");
    }
  };

  const handleSingleGenerate = async (p) => {
    try {
      const uId = isNaN(Number(p.user)) ? p.user : Number(p.user);
      await createPayroll({
        user: uId,
        month: selectedMonth,
        base_salary: parseFloat(p.base_salary) || 0,
        present_days: 0,
        late_days: 0,
        absent_days: 0,
        weekend_days: 0,
        holiday_days: 0,
        working_days: 30,
        overtime_hours: 0,
        overtime_pay: 0,
        allowances: 0,
        deductions: 0,
        net_salary: parseFloat(p.base_salary) || 0,
        payment_status: "Pending"
      }).unwrap();
    } catch (err) {
      console.error("Single generate error:", err);
      try {
        await generatePayroll({ month: selectedMonth }).unwrap();
      } catch (e) {
        toast.error(err?.data?.error || err?.data?.detail || "Failed to generate payroll");
      }
    }
  };

  const handleStatusChange = async (payroll, newStatus) => {
    try {
      await updatePayroll({
        id: payroll.id,
        payment_status: newStatus,
        payment_date: newStatus === "Paid" ? new Date().toISOString().slice(0, 10) : payroll.payment_date
      }).unwrap();
    } catch (err) {
      toast.error("Failed to update payment status");
    }
  };

  const viewPayslip = (payroll) => {
    setSelectedPayroll(payroll);
    setPayslipModalOpen(true);
  };

  const openSalaryModal = () => {
    setSalaryModalOpen(true);
  };

  const getEmpUserId = (e) => {
    if (!e || !e.user) return null;
    return typeof e.user === 'object' && e.user !== null ? e.user.id : e.user;
  };

  const handleUserSelectChange = (uId) => {
    setSelectedUserId(uId);
    const existingEmp = employees.find(e => String(getEmpUserId(e)) === String(uId));
    if (existingEmp) {
      setSalaryInput(existingEmp.salary || "");
      setDepartmentInput(existingEmp.department || "");
      setDesignationInput(existingEmp.designation || "");
    } else {
      setSalaryInput("");
      setDepartmentInput("");
      setDesignationInput("");
    }
  };

  const handleSaveEmployeeSalary = async () => {
    if (!selectedUserId || !salaryInput) {
      toast.error("Please select an employee and enter base salary");
      return;
    }
    const userIdVal = isNaN(Number(selectedUserId)) ? selectedUserId : Number(selectedUserId);
    const existingEmp = employees.find(e => String(getEmpUserId(e)) === String(selectedUserId));
    try {
      if (existingEmp) {
        await updateEmployee({
          id: existingEmp.id,
          user: userIdVal,
          salary: parseFloat(salaryInput),
          department: departmentInput,
          designation: designationInput
        }).unwrap();
      } else {
        await createEmployee({
          user: userIdVal,
          salary: parseFloat(salaryInput),
          department: departmentInput,
          designation: designationInput
        }).unwrap();
      }
      toast.success("Employee salary saved successfully");
      setSalaryModalOpen(false);
      setSelectedUserId("");
      setSalaryInput("");
    } catch (err) {
      console.error("Employee save error:", err);
      toast.error(err?.data?.detail || err?.data?.error || "Failed to save employee base salary");
    }
  };

  // Combine generated payroll rows with missing employees that have base salaries set
  const displayPayrollList = React.useMemo(() => {
    const list = [...payrollData];
    const existingUserIds = new Set(payrollData.map(p => String(p.user?.id || p.user)));

    employees.forEach(emp => {
      const empUserId = String(emp.user?.id || emp.user);
      if (!existingUserIds.has(empUserId)) {
        const userObj = users.find(u => String(u.id) === empUserId);
        if (userObj) {
          list.push({
            id: `temp-${emp.id}`,
            isDraft: true,
            user: empUserId,
            user_name: userObj.nickname || userObj.first_name || userObj.username || `User #${empUserId}`,
            emp_id: emp.id,
            month: selectedMonth,
            base_salary: emp.salary || 0,
            present_days: 0,
            late_days: 0,
            absent_days: 0,
            weekend_days: 0,
            holiday_days: 0,
            working_days: 30,
            overtime_hours: 0,
            overtime_pay: 0,
            allowances: 0,
            deductions: 0,
            net_salary: emp.salary || 0,
            payment_status: "Not Generated"
          });
        }
      }
    });

    return list;
  }, [payrollData, employees, users, selectedMonth]);

  // Filtered list based on role
  const filteredDisplayList = React.useMemo(() => {
    if (isAdmin) return displayPayrollList;
    return displayPayrollList.filter(p => String(p.user?.id || p.user) === String(user?.id));
  }, [displayPayrollList, isAdmin, user]);

  // KPI Calculations
  const userPayrollItem = filteredDisplayList[0] || null;
  const totalBaseSalary = isAdmin
    ? payrollData.reduce((acc, p) => acc + (parseFloat(p.base_salary) || 0), 0)
    : parseFloat(userPayrollItem?.base_salary || 0);
  const totalNetPayable = isAdmin
    ? payrollData.reduce((acc, p) => acc + (parseFloat(p.net_salary) || 0), 0)
    : parseFloat(userPayrollItem?.net_salary || 0);
  const paidCount = payrollData.filter(p => p.payment_status === "Paid").length;
  const pendingCount = payrollData.filter(p => p.payment_status === "Pending").length;

  if (isPayrollLoading) return <DashboardLoader />;

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumb title="HR & Payroll" breadcrumbItem={isAdmin ? "Payroll & Salary Management" : "My Payroll & Salary"} />

          {/* KPI CARDS */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="mini-stats-wid border-0 shadow-sm bg-primary text-white">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-white-50 fw-medium mb-1">{isAdmin ? "Total Net Salary" : "My Net Salary"}</p>
                      <h4 className="mb-0 text-white">৳ {totalNetPayable.toLocaleString()}</h4>
                    </div>
                    <div className="mini-stat-icon avatar-sm rounded-circle bg-white-50 align-self-center">
                      <span className="avatar-title bg-transparent text-white fs-4">
                        <i className="fas fa-money-bill-wave" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="mini-stats-wid border-0 shadow-sm bg-info text-white">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-white-50 fw-medium mb-1">{isAdmin ? "Total Base Salary" : "My Base Salary"}</p>
                      <h4 className="mb-0 text-white">৳ {totalBaseSalary.toLocaleString()}</h4>
                    </div>
                    <div className="mini-stat-icon avatar-sm rounded-circle bg-white-50 align-self-center">
                      <span className="avatar-title bg-transparent text-white fs-4">
                        <i className="fas fa-calculator" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="mini-stats-wid border-0 shadow-sm bg-success text-white">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-white-50 fw-medium mb-1">{isAdmin ? "Paid Salaries" : "Present / Late"}</p>
                      <h4 className="mb-0 text-white">
                        {isAdmin ? `${paidCount} Employees` : `${userPayrollItem?.present_days || 0} P / ${userPayrollItem?.late_days || 0} L`}
                      </h4>
                    </div>
                    <div className="mini-stat-icon avatar-sm rounded-circle bg-white-50 align-self-center">
                      <span className="avatar-title bg-transparent text-white fs-4">
                        <i className="fas fa-check-circle" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="mini-stats-wid border-0 shadow-sm bg-warning text-dark">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-dark-50 fw-medium mb-1">{isAdmin ? "Pending Payouts" : "Payment Status"}</p>
                      <h4 className="mb-0 text-dark">
                        {isAdmin ? `${pendingCount} Employees` : (userPayrollItem?.payment_status || "Pending")}
                      </h4>
                    </div>
                    <div className="mini-stat-icon avatar-sm rounded-circle bg-dark-50 align-self-center">
                      <span className="avatar-title bg-transparent text-dark fs-4">
                        <i className="fas fa-clock" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* MAIN TABLE */}
          <Card>
            <CardBody>
              <Row className="mb-3 align-items-center">
                <Col md={4} className="d-flex align-items-center">
                  <Label className="me-2 mb-0 fw-bold">Select Month:</Label>
                  <Input
                    type="month"
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                    style={{ maxWidth: "200px" }}
                  />
                </Col>
                {isAdmin && (
                  <Col md={8} className="text-end d-flex justify-content-end gap-2">
                    <Button color="primary" onClick={openSalaryModal}>
                      <i className="fas fa-user-tag me-2" /> Set Base Salaries
                    </Button>
                    <Button color="success" onClick={handleGeneratePayroll} disabled={isGenerating}>
                      <i className="fas fa-cog me-2" />
                      {isGenerating ? "Calculating..." : "Generate Monthly Payroll"}
                    </Button>
                  </Col>
                )}
              </Row>

              <div className="table-responsive">
                <Table hover striped className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Employee</th>
                      <th>Month</th>
                      <th>Base Salary</th>
                      <th>Target Bonus</th>
                      <th>Net Payable</th>
                      <th>Payment Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDisplayList.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          No payroll data found for {selectedMonth}.
                        </td>
                      </tr>
                    ) : (
                      filteredDisplayList.map(p => (
                        <tr key={p.id}>
                          <td className="fw-bold">{p.user_name || `User #${p.user}`}</td>
                          <td><code>{p.month}</code></td>
                          <td>৳ {parseFloat(p.base_salary).toLocaleString()}</td>
                          <td>
                            {parseFloat(p.allowances) > 0 ? (
                              <Badge color="success" className="px-2 py-1 fs-6">
                                +৳ {parseFloat(p.allowances).toLocaleString()}
                              </Badge>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="fw-bold text-primary fs-6">
                            ৳ {parseFloat(p.net_salary).toLocaleString()}
                          </td>
                          <td>
                            {p.payment_status === "Paid" ? (
                              <Badge color="success" className="px-2 py-1">Paid</Badge>
                            ) : p.payment_status === "Approved" ? (
                              <Badge color="info" className="px-2 py-1">Approved</Badge>
                            ) : p.payment_status === "Not Generated" ? (
                              <Badge color="secondary" className="px-2 py-1">Not Generated</Badge>
                            ) : (
                              <Badge color="warning" className="px-2 py-1">Pending</Badge>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              {p.isDraft ? (
                                isAdmin && (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleSingleGenerate(p)}
                                    disabled={isCreatingSingle || isGenerating}
                                    title="Generate Payroll for this Employee"
                                  >
                                    <i className="fas fa-magic me-1" />
                                    {isCreatingSingle ? "Generating..." : "Generate"}
                                  </Button>
                                )
                              ) : (
                                <>
                                  <Button color="light" size="sm" onClick={() => viewPayslip(p)} title="View Payslip">
                                    <i className="fas fa-file-invoice" />
                                  </Button>
                                  {isAdmin && p.payment_status !== "Paid" && (
                                    <Button color="success" size="sm" onClick={() => handleStatusChange(p, "Paid")} title="Mark as Paid">
                                      <i className="fas fa-check" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          {/* SET BASE SALARY MODAL */}
          <Modal isOpen={salaryModalOpen} toggle={() => setSalaryModalOpen(!salaryModalOpen)}>
            <ModalHeader toggle={() => setSalaryModalOpen(!salaryModalOpen)}>
              Set Employee Base Salary
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Select Employee</Label>
                <Input
                  type="select"
                  value={selectedUserId}
                  onChange={e => handleUserSelectChange(e.target.value)}
                >
                  <option value="">-- Choose Employee --</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.nickname || u.official_email || `User #${u.id}`} ({u.role})
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label>Monthly Base Salary (৳)</Label>
                <Input
                  type="number"
                  placeholder="e.g. 35000"
                  value={salaryInput}
                  onChange={e => setSalaryInput(e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Department (Optional)</Label>
                <Input
                  type="text"
                  placeholder="e.g. Software Development"
                  value={departmentInput}
                  onChange={e => setDepartmentInput(e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Designation (Optional)</Label>
                <Input
                  type="text"
                  placeholder="e.g. Senior Full Stack Developer"
                  value={designationInput}
                  onChange={e => setDesignationInput(e.target.value)}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setSalaryModalOpen(false)}>Cancel</Button>
              <Button color="primary" onClick={handleSaveEmployeeSalary} disabled={isCreatingEmp || isUpdatingEmp}>
                {isCreatingEmp || isUpdatingEmp ? "Saving..." : "Save Base Salary"}
              </Button>
            </ModalFooter>
          </Modal>

          {/* PAYSLIP DETAIL MODAL */}
          <Modal isOpen={payslipModalOpen} toggle={() => setPayslipModalOpen(!payslipModalOpen)} size="lg">
            <ModalHeader toggle={() => setPayslipModalOpen(!payslipModalOpen)}>
              Employee Payslip Summary
            </ModalHeader>
            <ModalBody>
              {selectedPayroll && (
                <div className="p-3 border rounded">
                  <div className="d-flex justify-content-between border-bottom pb-3 mb-3">
                    <div>
                      <h4 className="fw-bold mb-1">{selectedPayroll.user_name || `Employee #${selectedPayroll.user}`}</h4>
                      <p className="text-muted mb-0">ID: {selectedPayroll.emp_id || selectedPayroll.user}</p>
                    </div>
                    <div className="text-end">
                      <h5 className="fw-bold text-primary">Month: {selectedPayroll.month}</h5>
                      <Badge color={selectedPayroll.payment_status === "Paid" ? "success" : "warning"}>
                        {selectedPayroll.payment_status}
                      </Badge>
                    </div>
                  </div>

                  <Row className="mb-3">
                    <Col md={6}>
                      <h6 className="fw-bold border-bottom pb-2">Attendance Summary</h6>
                      <p className="mb-1">Total Calendar Days: <strong>{selectedPayroll.working_days}</strong></p>
                      <p className="mb-1 text-success">Present Days: <strong>{selectedPayroll.present_days}</strong></p>
                      <p className="mb-1 text-warning">Late Days: <strong>{selectedPayroll.late_days}</strong></p>
                      <p className="mb-1 text-danger">Absent Days: <strong>{selectedPayroll.absent_days}</strong></p>
                      <p className="mb-1 text-secondary">Weekend & Holidays: <strong>{selectedPayroll.weekend_days + selectedPayroll.holiday_days}</strong></p>
                    </Col>
                    <Col md={6}>
                      <h6 className="fw-bold border-bottom pb-2">Salary & Earnings</h6>
                      <p className="mb-1">Base Monthly Salary: <strong>৳ {parseFloat(selectedPayroll.base_salary).toLocaleString()}</strong></p>
                      <p className="mb-1 text-success">Overtime Pay ({selectedPayroll.overtime_hours} hrs): <strong>+৳ {parseFloat(selectedPayroll.overtime_pay).toLocaleString()}</strong></p>
                      <p className="mb-1 text-success">15% Target Excess Bonus: <strong>+৳ {parseFloat(selectedPayroll.allowances).toLocaleString()}</strong></p>
                      <p className="mb-1 text-danger">Deductions (Absent/Late): <strong>-৳ {parseFloat(selectedPayroll.deductions).toLocaleString()}</strong></p>
                      <hr />
                      <h5 className="fw-bold text-primary">Net Payable Salary: ৳ {parseFloat(selectedPayroll.net_salary).toLocaleString()}</h5>
                    </Col>
                  </Row>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setPayslipModalOpen(false)}>Close</Button>
            </ModalFooter>
          </Modal>

        </div>
      </div>
    </React.Fragment>
  );
};

export default PayrollManagement;
